import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	Button,
	Callout,
	GraphQLErrorMessage,
	Heading,
	Modal,
	Paragraph,
	Popover,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { ConversationRevealEmptySaveConfirm } from './ConversationRevealEmptySaveConfirm';
import { ConversationRevealFieldPreview } from './ConversationRevealFieldPreview';
import { ConversationRevealProcessingRows } from './ConversationRevealProcessingRows';
import { ConversationRevealUploadSummary } from './ConversationRevealUploadSummary';
import {
	ACCEPTED_FILE_TYPES,
	PendingRevealUpload,
	UploadStep,
	useConversationRevealUpload,
} from './useConversationRevealUpload';

const DropZone = styled(Box)<{ dragging: boolean }>`
	border: 2px dashed ${(props) => props.theme.colors.primary};
	border-radius: 6px;
	text-align: center;
	${(props) => props.dragging && `border-style: solid; background: ${tokens.surface.secondary};`}
`;

const SelectedFile = styled(Flex)`
	background: ${tokens.surface.secondary};
	border-radius: 6px;
`;

const formatFileSize = (bytes: number) =>
	bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.ceil(bytes / 1000)} kB`;

interface IProps {
	evaluationFormID?: string;
	onComplete?: (pending: PendingRevealUpload) => void;
	onClose: () => void;
	onBusyChange?: (busy: boolean) => void;
}

export function ConversationRevealUploadForm({
	evaluationFormID,
	onComplete,
	onClose,
	onBusyChange,
}: IProps) {
	const { t } = useTranslation();
	const [confirmingEmptySave, setConfirmingEmptySave] = useState(false);

	const {
		inputRef,
		step,
		file,
		summary,
		selectedFields,
		fieldTypes,
		dragging,
		loading,
		error,
		canProcess,
		canContinue,
		canSave,
		wouldWipeOnSave,
		handleFileInputChange,
		handleBrowseClick,
		handleDragEnter,
		handleDragOver,
		handleDragLeave,
		handleDrop,
		excludedUserIDs,
		handleToggleField,
		handleChangeFieldType,
		handleToggleUser,
		handleBack,
		handleNext,
		handleProcess,
		handleSave,
	} = useConversationRevealUpload({ evaluationFormID, onComplete, onClose, onBusyChange });

	const handleSummarySave = async () => {
		if (wouldWipeOnSave) {
			setConfirmingEmptySave(true);
			return;
		}

		try {
			await handleSave();
		} catch {
			// error already handled in handleSave via handleError
		}
	};

	const handleConfirmEmptySave = () => handleSave({ force: true });

	const handleDismissEmptySave = () => {
		if (!loading) {
			setConfirmingEmptySave(false);
		}
	};

	const footerByStep = {
		[UploadStep.SELECT]: {
			onSave: handleProcess,
			disabled: !canProcess,
			saveTitle: t(
				file ? 'conversations.reveal.upload.uploadFile' : 'conversations.reveal.upload.process',
			),
			onCancel: onClose,
			cancelTitle: undefined,
		},
		[UploadStep.FIELDS]: {
			onSave: handleNext,
			disabled: !canContinue,
			saveTitle: t('conversations.reveal.upload.next'),
			onCancel: handleBack,
			cancelTitle: t('conversations.reveal.upload.previous'),
		},
		[UploadStep.SUMMARY]: {
			onSave: handleSummarySave,
			disabled: !canSave,
			saveTitle: t('conversations.reveal.upload.save'),
			onCancel: handleBack,
			cancelTitle: t('conversations.reveal.upload.previous'),
		},
	}[step];

	return (
		<>
			<Modal width={720}>
				<Modal.Header title={t('conversations.reveal.upload.title')} onClose={onClose} />

				<Modal.Contents>
					{typeof error === 'string' && (
						<Callout color="danger" mb={3}>
							{error}
						</Callout>
					)}
					{error !== undefined && typeof error !== 'string' && (
						<GraphQLErrorMessage error={error} />
					)}

					{loading && step === UploadStep.SELECT && (
						<Box py={3}>
							<ConversationRevealProcessingRows />
							<Paragraph color="slateBlue" mt={3} style={{ textAlign: 'center' }}>
								{t('conversations.reveal.upload.processing')}
							</Paragraph>
						</Box>
					)}

					{loading && step !== UploadStep.SELECT && (
						<Flex justifyContent="center" py={4}>
							<Spinner />
						</Flex>
					)}

					{!loading && step === UploadStep.SELECT && (
						<Box>
							<Callout color="blue" mb={3}>
								{t('conversations.reveal.upload.onlyNextConversation')}
							</Callout>

							<input
								ref={inputRef}
								type="file"
								accept={ACCEPTED_FILE_TYPES.join(', ')}
								style={{ display: 'none' }}
								onChange={handleFileInputChange}
							/>

							<DropZone
								dragging={dragging}
								p={4}
								onDragEnter={handleDragEnter}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
							>
								<Heading color="primary" mb={3}>
									{t('conversations.reveal.upload.dropHere')}
								</Heading>
								<Button
									type="secondary"
									title={t('conversations.reveal.upload.browse')}
									onClick={handleBrowseClick}
								/>
							</DropZone>

							{file && (
								<SelectedFile mt={3} p={3} alignItems="center" justifyContent="space-between">
									<Paragraph fontWeight="bold">{file.name}</Paragraph>
									<Paragraph color="slateBlue">{formatFileSize(file.size)}</Paragraph>
								</SelectedFile>
							)}

							<Paragraph color="slateBlue" mt={3}>
								{t('conversations.reveal.upload.hint')}
							</Paragraph>
						</Box>
					)}

					{!loading && step === UploadStep.FIELDS && summary && (
						<ConversationRevealFieldPreview
							summary={summary}
							selectedFields={selectedFields}
							fieldTypes={fieldTypes}
							onToggleField={handleToggleField}
							onChangeFieldType={handleChangeFieldType}
						/>
					)}

					{!loading && step === UploadStep.SUMMARY && summary && (
						<ConversationRevealUploadSummary
							summary={summary}
							selectedFields={selectedFields}
							fieldTypes={fieldTypes}
							excludedUserIDs={excludedUserIDs}
							onToggleUser={handleToggleUser}
						/>
					)}
				</Modal.Contents>

				<Modal.FormFooter
					onCancel={footerByStep.onCancel}
					cancelTitle={footerByStep.cancelTitle}
					onSave={footerByStep.onSave}
					disabled={footerByStep.disabled}
					saveTitle={footerByStep.saveTitle}
				/>
			</Modal>

			<Popover.Target
				backdrop
				center
				visible={confirmingEmptySave}
				onDismiss={handleDismissEmptySave}
				contents={
					confirmingEmptySave ? (
						<ConversationRevealEmptySaveConfirm
							saving={loading}
							onConfirm={handleConfirmEmptySave}
							onClose={() => setConfirmingEmptySave(false)}
						/>
					) : null
				}
			>
				<span />
			</Popover.Target>
		</>
	);
}
